import React, { useEffect, useMemo, useState } from "react";
import FoundryPageShell from "../components/foundry/FoundryPageShell";

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

type LandscapePageProps = {
  apiFetch: ApiFetch;
};

type Finding = {
  ruleId: string;
  severity: "low" | "medium" | "high";
  title: string;
  recommendation: string;
};

type Resource = {
  id?: string;
  name: string;
  type: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName?: string;
  location?: string;
  tags?: Record<string, string>;
  monthlyCost?: number;
  currency?: string;
  governanceScore?: number;
  findings: Finding[];
};

type Subscription = {
  subscriptionId: string;
  subscriptionName: string;
  resourceCount: number;
  monthlyCost: number;
  findingCount: number;
  governanceScore: number;
};

type ResourceGroup = {
  subscriptionId: string;
  subscriptionName?: string;
  resourceGroup: string;
  resourceCount: number;
  monthlyCost: number;
  findingCount: number;
  governanceScore: number;
};

type DependencyNode = {
  id: string;
  azureId: string;
  name: string;
  type: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName?: string;
  location?: string;
};

type DependencyEdge = {
  from: string;
  to: string;
  relation: string;
  source: string;
  confidence: "low" | "medium" | "high";
};

type DependenciesResponse = {
  scan: {
    scannedAt: string;
    source: string;
  };
  nodes: DependencyNode[];
  edges: DependencyEdge[];
  summaries: {
    nodeCount: number;
    edgeCount: number;
    highConfidenceEdges: number;
    mediumConfidenceEdges: number;
    lowConfidenceEdges: number;
  };
};

type OverviewResponse = {
  scan: {
    scannedAt: string;
    source: string;
    costStatus: string;
    costErrors?: unknown[];
  };
  summaries: {
    resourceCount: number;
    subscriptionCount: number;
    resourceGroupCount: number;
    findingCount: number;
    totalCost: number;
    currency: string;
    averageGovernanceScore: number;
  };
  subscriptions: Subscription[];
  resourceGroups: ResourceGroup[];
  resources: Resource[];
  findings: any[];
};

type ResourceCategory = {
  key: string;
  label: string;
  tone: string;
  icon: string;
  description: string;
};

type ResourceSort = "cost-desc" | "cost-asc" | "name";

type ResourceDetailsResponse = {
  found: boolean;
  scannedAt?: string;
  source?: string;
  kind?: string;
  resource?: {
    id: string;
    name: string;
    type: string;
    kind?: string;
    resourceGroup: string;
    subscriptionId: string;
    subscriptionName?: string;
    location?: string;
    tags?: Record<string, string>;
    sku?: any;
  };
  details?: any;
  error?: string;
};

function shortType(type: string) {
  const parts = String(type || "").split("/");
  return parts[parts.length - 1] || type || "Unknown";
}

function normalizeId(value?: string) {
  return String(value || "").toLowerCase();
}

function formatCurrency(value?: number, currency = "EUR") {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;

  return `${currency} ${safeValue.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value?: string) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("nl-NL");
}

function severityClass(severity: string) {
  if (severity === "high") return "bg-red-50 text-red-600";
  if (severity === "medium") return "bg-amber-50 text-amber-700";
  return "bg-blue-50 text-blue-600";
}

function confidenceClass(confidence: string) {
  if (confidence === "high") return "bg-emerald-50 text-emerald-700";
  if (confidence === "medium") return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

function getResourceCategory(type?: string): ResourceCategory {
  const value = String(type || "").toLowerCase();

  if (value.includes("microsoft.web/sites")) {
    return {
      key: "app-service",
      label: "Function/App Service",
      tone: "bg-purple-50 text-purple-700",
      icon: "⚡",
      description: "Function Apps en App Services",
    };
  }

  if (value.includes("microsoft.sql/servers/databases")) {
    return {
      key: "sql-database",
      label: "SQL Database",
      tone: "bg-blue-50 text-blue-700",
      icon: "🗄️",
      description: "Azure SQL databases",
    };
  }

  if (value.includes("microsoft.sql/servers")) {
    return {
      key: "sql-server",
      label: "SQL Server",
      tone: "bg-indigo-50 text-indigo-700",
      icon: "🧱",
      description: "Azure SQL logical servers",
    };
  }

  if (value.includes("microsoft.storage/storageaccounts")) {
    return {
      key: "storage",
      label: "Storage Account",
      tone: "bg-amber-50 text-amber-700",
      icon: "📦",
      description: "Blob, queue, table en file storage",
    };
  }

  if (value.includes("microsoft.keyvault/vaults")) {
    return {
      key: "key-vault",
      label: "Key Vault",
      tone: "bg-emerald-50 text-emerald-700",
      icon: "🔐",
      description: "Secrets, keys en certificates",
    };
  }

  if (value.includes("microsoft.logic/workflows")) {
    return {
      key: "logic-app",
      label: "Logic App",
      tone: "bg-orange-50 text-orange-700",
      icon: "🔁",
      description: "Logic App workflows",
    };
  }

  if (value.includes("microsoft.datafactory/factories")) {
    return {
      key: "data-factory",
      label: "Data Factory",
      tone: "bg-cyan-50 text-cyan-700",
      icon: "🏭",
      description: "ADF factories en data pipelines",
    };
  }

  if (value.includes("microsoft.insights/components")) {
    return {
      key: "app-insights",
      label: "Application Insights",
      tone: "bg-pink-50 text-pink-700",
      icon: "📈",
      description: "Application monitoring",
    };
  }

  if (value.includes("microsoft.operationalinsights/workspaces")) {
    return {
      key: "log-analytics",
      label: "Log Analytics",
      tone: "bg-sky-50 text-sky-700",
      icon: "🧾",
      description: "Log Analytics workspaces",
    };
  }

  if (value.includes("microsoft.managedidentity")) {
    return {
      key: "managed-identity",
      label: "Managed Identity",
      tone: "bg-teal-50 text-teal-700",
      icon: "🪪",
      description: "User assigned managed identities",
    };
  }

  if (value.includes("microsoft.containerregistry/registries")) {
    return {
      key: "container-registry",
      label: "Container Registry",
      tone: "bg-violet-50 text-violet-700",
      icon: "🐳",
      description: "Azure Container Registry",
    };
  }

  if (value.includes("microsoft.insights")) {
    return {
      key: "monitoring",
      label: "Monitoring",
      tone: "bg-slate-100 text-slate-700",
      icon: "📊",
      description: "Azure Monitor resources",
    };
  }

  return {
    key: "other",
    label: shortType(type || "Unknown"),
    tone: "bg-slate-100 text-slate-700",
    icon: "◼",
    description: "Overige Azure resource types",
  };
}

function getFriendlyResourceName(
  resource: Resource | DependencyNode | undefined,
) {
  if (!resource) return "-";

  const name = resource.name || "-";
  const type = resource.type?.toLowerCase() || "";

  if (type.includes("microsoft.operationalinsights/workspaces")) {
    if (name.startsWith("DefaultWorkspace-")) {
      const suffix = name.split("-").slice(-1)[0];
      return suffix
        ? `Default Log Analytics Workspace · ${suffix}`
        : "Default Log Analytics Workspace";
    }
  }

  if (type.includes("microsoft.insights")) {
    if (name.startsWith("MSVMI-DefaultWorkspace-")) {
      const suffix = name.split("-").slice(-1)[0];
      return suffix
        ? `Monitoring Workspace Insights · ${suffix}`
        : "Monitoring Workspace Insights";
    }
  }

  if (type.includes("microsoft.sql/servers/databases")) {
    return `SQL Database · ${name}`;
  }

  if (type.includes("microsoft.sql/servers")) {
    return `SQL Server · ${name}`;
  }

  if (type.includes("microsoft.storage/storageaccounts")) {
    return `Storage Account · ${name}`;
  }

  if (type.includes("microsoft.web/sites")) {
    return `Function/App Service · ${name}`;
  }

  if (type.includes("microsoft.logic/workflows")) {
    return `Logic App · ${name}`;
  }

  if (type.includes("microsoft.datafactory/factories")) {
    return `Data Factory · ${name}`;
  }

  if (type.includes("microsoft.keyvault/vaults")) {
    return `Key Vault · ${name}`;
  }

  return name;
}

function sortResources(resources: Resource[], sort: ResourceSort) {
  return [...resources].sort((a, b) => {
    if (sort === "cost-desc") {
      return (b.monthlyCost || 0) - (a.monthlyCost || 0);
    }

    if (sort === "cost-asc") {
      return (a.monthlyCost || 0) - (b.monthlyCost || 0);
    }

    return getFriendlyResourceName(a).localeCompare(
      getFriendlyResourceName(b),
      "nl-NL",
    );
  });
}

function DetailValue({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 break-all text-xs font-semibold text-slate-800">
        {value ?? "-"}
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-2xl bg-slate-50 dark:bg-slate-800 p-3 text-xs text-slate-500 dark:text-slate-400">
      {text}
    </div>
  );
}

function ResourceDetailsPanel({
  details,
  loading,
  error,
}: {
  details: ResourceDetailsResponse | null;
  loading: boolean;
  error: string;
}) {
  if (loading) {
    return (
      <section className="rounded-[32px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-sm font-bold text-slate-950 dark:text-slate-100">
            Live Resource Details
          </h2>
        </div>
        <div className="p-5 text-sm text-slate-500">Details laden...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-[32px] border border-amber-200 bg-amber-50 shadow-sm">
        <div className="px-5 py-4 text-sm text-amber-700">
          Resource details konden niet worden geladen: {error}
        </div>
      </section>
    );
  }

  if (!details) {
    return (
      <section className="rounded-[32px] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-sm font-bold text-slate-950">
            Live Resource Details
          </h2>
        </div>
        <div className="p-5 text-sm text-slate-500">
          Selecteer een resource om ARM-details uit te lezen.
        </div>
      </section>
    );
  }

  const d = details.details || {};
  const errors = d.errors || [];

  return (
    <section className="rounded-[32px] border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
          Live Resource Details
        </div>
        <h2 className="mt-2 text-sm font-bold text-slate-950">
          {details.kind || "resource"} · {details.source || "ARM"}
        </h2>
      </div>

      <div className="space-y-5 p-5">
        {details.kind === "webSite" && (
          <>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <DetailValue label="State" value={d.site?.state} />
              <DetailValue
                label="HTTPS Only"
                value={String(d.site?.httpsOnly ?? "-")}
              />
              <DetailValue
                label="Always On"
                value={String(d.runtime?.alwaysOn ?? "-")}
              />
              <DetailValue
                label="Runtime Linux"
                value={d.runtime?.linuxFxVersion || "-"}
              />
              <DetailValue label="Node" value={d.runtime?.nodeVersion || "-"} />
              <DetailValue
                label="Min TLS"
                value={d.runtime?.minTlsVersion || "-"}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Functions
              </div>
              {d.functions?.length ? (
                <div className="space-y-2">
                  {d.functions.slice(0, 8).map((fn: any) => (
                    <div
                      key={fn.name}
                      className="rounded-2xl border border-slate-200 dark:border-slate-700 p-3"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        {fn.functionName || fn.name}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {fn.language || "unknown"} · disabled:{" "}
                        {String(fn.isDisabled ?? false)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen functions gevonden of geen rechten." />
              )}
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                App Settings Keys
              </div>
              {d.appSettings?.length ? (
                <div className="flex flex-wrap gap-2">
                  {d.appSettings.slice(0, 24).map((setting: any) => (
                    <span
                      key={setting.name}
                      className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-[10px] font-bold text-slate-600 dark:text-slate-400"
                    >
                      {setting.name}
                    </span>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen app settings keys gevonden of geen rechten." />
              )}
            </div>
          </>
        )}

        {details.kind === "sqlServer" && (
          <>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <DetailValue label="Version" value={d.server?.version} />
              <DetailValue label="State" value={d.server?.state} />
              <DetailValue
                label="Public Network"
                value={d.server?.publicNetworkAccess}
              />
              <DetailValue
                label="Minimal TLS"
                value={d.server?.minimalTlsVersion}
              />
              <DetailValue label="Databases" value={d.databases?.length || 0} />
              <DetailValue
                label="Firewall rules"
                value={d.firewallRules?.length || 0}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Databases
              </div>
              {d.databases?.length ? (
                <div className="space-y-2">
                  {d.databases.map((db: any) => (
                    <div
                      key={db.id || db.name}
                      className="rounded-2xl border border-slate-200 p-3"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        SQL Database · {db.name}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {db.status || "-"} ·{" "}
                        {db.sku?.name || db.sku?.tier || "no sku"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen databases gevonden." />
              )}
            </div>
          </>
        )}

        {details.kind === "sqlDatabase" && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            <DetailValue label="Status" value={d.database?.status} />
            <DetailValue
              label="SKU"
              value={d.database?.sku?.name || d.database?.sku?.tier}
            />
            <DetailValue label="Collation" value={d.database?.collation} />
            <DetailValue
              label="Zone redundant"
              value={String(d.database?.zoneRedundant ?? "-")}
            />
            <DetailValue label="Read scale" value={d.database?.readScale} />
            <DetailValue
              label="Created"
              value={formatDateTime(d.database?.creationDate)}
            />
          </div>
        )}

        {details.kind === "storageAccount" && (
          <>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <DetailValue label="SKU" value={d.account?.sku?.name} />
              <DetailValue label="Kind" value={d.account?.kind} />
              <DetailValue label="Access tier" value={d.account?.accessTier} />
              <DetailValue
                label="Public network"
                value={d.account?.publicNetworkAccess}
              />
              <DetailValue
                label="Blob public access"
                value={String(d.account?.allowBlobPublicAccess ?? "-")}
              />
              <DetailValue
                label="Min TLS"
                value={d.account?.minimumTlsVersion}
              />
              <DetailValue
                label="HTTPS only"
                value={String(d.account?.supportsHttpsTrafficOnly ?? "-")}
              />
              <DetailValue
                label="Shared key"
                value={String(d.account?.allowSharedKeyAccess ?? "-")}
              />
              <DetailValue
                label="Containers"
                value={d.containers?.length || 0}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Containers
              </div>
              {d.containers?.length ? (
                <div className="space-y-2">
                  {d.containers.slice(0, 10).map((container: any) => (
                    <div
                      key={container.name}
                      className="rounded-2xl border border-slate-200 p-3"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        {container.name}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        public: {container.publicAccess || "None"} · lease:{" "}
                        {container.leaseState || "-"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen containers gevonden of geen rechten." />
              )}
            </div>
          </>
        )}

        {details.kind === "keyVault" && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            <DetailValue label="SKU" value={d.vault?.sku?.name} />
            <DetailValue
              label="RBAC"
              value={String(d.vault?.enableRbacAuthorization ?? "-")}
            />
            <DetailValue
              label="Soft delete"
              value={String(d.vault?.enableSoftDelete ?? "-")}
            />
            <DetailValue
              label="Purge protection"
              value={String(d.vault?.enablePurgeProtection ?? "-")}
            />
            <DetailValue
              label="Public network"
              value={d.vault?.publicNetworkAccess}
            />
            <DetailValue
              label="Access policies"
              value={d.vault?.accessPolicyCount ?? 0}
            />
            <DetailValue
              label="Network default"
              value={d.vault?.networkAcls?.defaultAction}
            />
            <DetailValue
              label="IP rules"
              value={d.vault?.networkAcls?.ipRuleCount ?? 0}
            />
            <DetailValue
              label="VNet rules"
              value={d.vault?.networkAcls?.virtualNetworkRuleCount ?? 0}
            />
          </div>
        )}

        {details.kind === "logicWorkflow" && (
          <>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <DetailValue label="State" value={d.workflow?.state} />
              <DetailValue
                label="Provisioning"
                value={d.workflow?.provisioningState}
              />
              <DetailValue
                label="Triggers"
                value={d.workflow?.triggerCount ?? 0}
              />
              <DetailValue
                label="Actions"
                value={d.workflow?.actionCount ?? 0}
              />
              <DetailValue
                label="Parameters"
                value={d.workflow?.parameterCount ?? 0}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Actions
              </div>
              {d.workflow?.actions?.length ? (
                <div className="space-y-2">
                  {d.workflow.actions.slice(0, 20).map((action: any) => (
                    <div
                      key={action.name}
                      className="rounded-2xl border border-slate-200 p-3"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        {action.name}
                      </div>

                      <div className="mt-1 text-[11px] text-slate-500">
                        {action.type || "Unknown"}
                        {action.connector ? ` · ${action.connector}` : ""}
                        {action.method ? ` · ${action.method}` : ""}
                      </div>

                      {action.connectionName && (
                        <div className="mt-1 break-all text-[10px] text-slate-400">
                          Connection: {action.connectionName}
                        </div>
                      )}

                      {action.path && (
                        <div className="mt-1 break-all text-[10px] text-slate-400">
                          Path: {action.path}
                        </div>
                      )}

                      {action.apiId && (
                        <div className="mt-1 break-all text-[10px] text-slate-300">
                          API: {action.apiId}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen actions gevonden." />
              )}
            </div>
          </>
        )}

        {details.kind === "dataFactory" && (
          <>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
              <DetailValue
                label="Provisioning"
                value={d.factory?.provisioningState}
              />
              <DetailValue
                label="Public network"
                value={d.factory?.publicNetworkAccess}
              />
              <DetailValue label="Pipelines" value={d.pipelines?.length || 0} />
              <DetailValue label="Datasets" value={d.datasets?.length || 0} />
              <DetailValue
                label="Linked services"
                value={d.linkedServices?.length || 0}
              />
              <DetailValue label="Triggers" value={d.triggers?.length || 0} />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Pipelines
              </div>
              {d.pipelines?.length ? (
                <div className="space-y-2">
                  {d.pipelines.slice(0, 10).map((pipeline: any) => (
                    <div
                      key={pipeline.name}
                      className="rounded-2xl border border-slate-200 p-3"
                    >
                      <div className="text-xs font-bold text-slate-900">
                        {pipeline.name}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {pipeline.activityCount} activities ·{" "}
                        {pipeline.parameterCount} parameters
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="Geen pipelines gevonden." />
              )}
            </div>
          </>
        )}

        {details.kind === "applicationInsights" && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
            <DetailValue
              label="Application type"
              value={d.component?.applicationType}
            />
            <DetailValue
              label="Provisioning"
              value={d.component?.provisioningState}
            />
            <DetailValue
              label="Workspace"
              value={d.component?.workspaceResourceId}
            />
            <DetailValue
              label="Retention"
              value={d.component?.retentionInDays}
            />
            <DetailValue
              label="Local auth disabled"
              value={String(d.component?.disableLocalAuth ?? "-")}
            />
            <DetailValue
              label="Query access"
              value={d.component?.publicNetworkAccessForQuery}
            />
          </div>
        )}

        {details.kind === "generic" && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <DetailValue label="Kind" value={d.generic?.kind || "-"} />
            <DetailValue label="SKU" value={d.generic?.sku?.name || "-"} />
            <DetailValue
              label="Identity"
              value={d.generic?.identity?.type || "-"}
            />
            <DetailValue label="Location" value={d.generic?.location || "-"} />
          </div>
        )}

        {errors.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-amber-700">
              Partial detail errors
            </div>
            <div className="mt-2 space-y-1">
              {errors.map((item: any) => (
                <div key={item.scope} className="text-[11px] text-amber-700">
                  <span className="font-bold">{item.scope}</span>: {item.error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const LandscapePage: React.FC<LandscapePageProps> = ({ apiFetch }) => {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [dependencies, setDependencies] = useState<DependenciesResponse | null>(
    null,
  );

  const [loading, setLoading] = useState(true);
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependenciesError, setDependenciesError] = useState("");

  const [selectedSubscription, setSelectedSubscription] = useState<
    string | null
  >(null);
  const [selectedResourceGroup, setSelectedResourceGroup] = useState<
    string | null
  >(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [selectedTypeKey, setSelectedTypeKey] = useState<string | null>(null);
  const [expandedTypeKey, setExpandedTypeKey] = useState<string | null>(null);
  const [resourceSort, setResourceSort] = useState<ResourceSort>("cost-desc");
  const [search, setSearch] = useState("");

  const [resourceDetails, setResourceDetails] =
    useState<ResourceDetailsResponse | null>(null);
  const [resourceDetailsLoading, setResourceDetailsLoading] = useState(false);
  const [resourceDetailsError, setResourceDetailsError] = useState("");

  const [openSubscriptions, setOpenSubscriptions] = useState<
    Record<string, boolean>
  >({});
  const [openResourceGroups, setOpenResourceGroups] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setDependenciesLoading(true);
        setError("");
        setDependenciesError("");

        // These scans are independent. Starting them together avoids making the
        // dependency graph wait for the (potentially slower) cost overview.
        const overviewRequest = apiFetch("/api/foundry/landscape/overview");
        const dependenciesRequest = apiFetch("/api/foundry/landscape/dependencies");
        const overviewRes = await overviewRequest;

        if (!overviewRes.ok) {
          const json = await overviewRes.json().catch(() => null);
          throw new Error(
            json?.detail || json?.error || "Landscape overview laden mislukt",
          );
        }

        const overviewData = await overviewRes.json();

        if (!cancelled) {
          setOverview({
            ...overviewData,
            subscriptions: overviewData.subscriptions || [],
            resourceGroups: overviewData.resourceGroups || [],
            resources: overviewData.resources || [],
            findings: overviewData.findings || [],
          });

          const initialSubscriptions: Record<string, boolean> = {};
          const initialGroups: Record<string, boolean> = {};

          (overviewData.subscriptions || []).forEach(
            (subscription: Subscription) => {
              initialSubscriptions[subscription.subscriptionId] = true;
            },
          );

          (overviewData.resourceGroups || []).forEach(
            (group: ResourceGroup) => {
              initialGroups[`${group.subscriptionId}/${group.resourceGroup}`] =
                false;
            },
          );

          setOpenSubscriptions(initialSubscriptions);
          setOpenResourceGroups(initialGroups);
        }

        try {
          const depsRes = await dependenciesRequest;

          if (!depsRes.ok) {
            const json = await depsRes.json().catch(() => null);
            throw new Error(
              json?.detail || json?.error || "Dependencies laden mislukt",
            );
          }

          const depsData = await depsRes.json();

          if (!cancelled) {
            setDependencies({
              ...depsData,
              nodes: depsData.nodes || [],
              edges: depsData.edges || [],
            });
          }
        } catch (err: any) {
          if (!cancelled) {
            setDependenciesError(err?.message || "Dependencies laden mislukt");
          }
        } finally {
          if (!cancelled) {
            setDependenciesLoading(false);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Onbekende fout");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDependenciesLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  useEffect(() => {
    let cancelled = false;

    async function loadResourceDetails() {
      if (!selectedResource?.id) {
        setResourceDetails(null);
        setResourceDetailsError("");
        setResourceDetailsLoading(false);
        return;
      }

      try {
        setResourceDetailsLoading(true);
        setResourceDetailsError("");

        const res = await apiFetch(
          `/api/foundry/landscape/resource-details?resourceId=${encodeURIComponent(
            selectedResource.id,
          )}`,
        );

        const json = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(
            json?.detail || json?.error || "Resource details laden mislukt",
          );
        }

        if (!cancelled) {
          setResourceDetails(json);
        }
      } catch (err: any) {
        if (!cancelled) {
          setResourceDetails(null);
          setResourceDetailsError(
            err?.message || "Resource details laden mislukt",
          );
        }
      } finally {
        if (!cancelled) {
          setResourceDetailsLoading(false);
        }
      }
    }

    loadResourceDetails();

    return () => {
      cancelled = true;
    };
  }, [apiFetch, selectedResource?.id]);

  const resourcesInSelectedScopeBeforeType = useMemo(() => {
    if (!overview) return [];

    if (selectedResource) return [selectedResource];

    if (selectedResourceGroup && selectedSubscription) {
      return overview.resources.filter(
        (r) =>
          r.subscriptionId === selectedSubscription &&
          r.resourceGroup === selectedResourceGroup,
      );
    }

    if (selectedSubscription) {
      return overview.resources.filter(
        (r) => r.subscriptionId === selectedSubscription,
      );
    }

    return overview.resources;
  }, [overview, selectedSubscription, selectedResourceGroup, selectedResource]);

  const selectedScopeResources = useMemo(() => {
    const scoped = selectedTypeKey
      ? resourcesInSelectedScopeBeforeType.filter(
          (resource) =>
            getResourceCategory(resource.type).key === selectedTypeKey,
        )
      : resourcesInSelectedScopeBeforeType;

    return sortResources(scoped, resourceSort);
  }, [resourcesInSelectedScopeBeforeType, selectedTypeKey, resourceSort]);

  const filteredResources = useMemo(() => {
    if (!overview) return [];

    let resources = [...overview.resources];

    if (selectedSubscription) {
      resources = resources.filter(
        (r) => r.subscriptionId === selectedSubscription,
      );
    }

    if (selectedResourceGroup) {
      resources = resources.filter(
        (r) =>
          r.subscriptionId === selectedSubscription &&
          r.resourceGroup === selectedResourceGroup,
      );
    }

    if (selectedTypeKey) {
      resources = resources.filter(
        (r) => getResourceCategory(r.type).key === selectedTypeKey,
      );
    }

    if (search.trim()) {
      const q = search.toLowerCase();

      resources = resources.filter((r) => {
        const category = getResourceCategory(r.type);
        const friendlyName = getFriendlyResourceName(r);

        return (
          r.name.toLowerCase().includes(q) ||
          friendlyName.toLowerCase().includes(q) ||
          r.type.toLowerCase().includes(q) ||
          category.label.toLowerCase().includes(q) ||
          category.description.toLowerCase().includes(q) ||
          r.resourceGroup.toLowerCase().includes(q) ||
          (r.subscriptionName || "").toLowerCase().includes(q) ||
          r.subscriptionId.toLowerCase().includes(q)
        );
      });
    }

    return sortResources(resources, resourceSort);
  }, [
    overview,
    selectedSubscription,
    selectedResourceGroup,
    selectedTypeKey,
    search,
    resourceSort,
  ]);

  const selectedScopeFindings = useMemo(() => {
    return selectedScopeResources.flatMap((resource) =>
      (resource.findings || []).map((finding) => ({
        ...finding,
        resourceId: resource.id,
        resourceName: resource.name,
        resourceFriendlyName: getFriendlyResourceName(resource),
        resourceType: resource.type,
        resourceGroup: resource.resourceGroup,
        subscriptionId: resource.subscriptionId,
        subscriptionName: resource.subscriptionName,
      })),
    );
  }, [selectedScopeResources]);

  const resourceTypeSummary = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        label: string;
        icon: string;
        tone: string;
        description: string;
        count: number;
        monthlyCost: number;
        findings: number;
      }
    >();

    resourcesInSelectedScopeBeforeType.forEach((resource) => {
      const category = getResourceCategory(resource.type);

      const current = map.get(category.key) || {
        key: category.key,
        label: category.label,
        icon: category.icon,
        tone: category.tone,
        description: category.description,
        count: 0,
        monthlyCost: 0,
        findings: 0,
      };

      current.count += 1;
      current.monthlyCost += resource.monthlyCost || 0;
      current.findings += resource.findings?.length || 0;

      map.set(category.key, current);
    });

    return [...map.values()].sort((a, b) => b.monthlyCost - a.monthlyCost);
  }, [resourcesInSelectedScopeBeforeType]);

  const dependencyContext = useMemo(() => {
    if (!dependencies) {
      return {
        dependsOn: [] as { edge: DependencyEdge; node?: DependencyNode }[],
        usedBy: [] as { edge: DependencyEdge; node?: DependencyNode }[],
        sameSolution: [] as { edge: DependencyEdge; node?: DependencyNode }[],
        visibleEdges: [] as DependencyEdge[],
      };
    }

    const nodeMap = new Map(dependencies.nodes.map((node) => [node.id, node]));
    const relevantResourceIds = new Set<string>();

    if (selectedResource?.id) {
      relevantResourceIds.add(normalizeId(selectedResource.id));
    } else {
      selectedScopeResources.forEach((resource) => {
        if (resource.id) relevantResourceIds.add(normalizeId(resource.id));
      });
    }

    const visibleEdges = dependencies.edges.filter(
      (edge) =>
        relevantResourceIds.has(edge.from) || relevantResourceIds.has(edge.to),
    );

    const dependsOn = visibleEdges
      .filter(
        (edge) =>
          relevantResourceIds.has(edge.from) &&
          edge.relation !== "same-solution",
      )
      .map((edge) => ({ edge, node: nodeMap.get(edge.to) }));

    const usedBy = visibleEdges
      .filter(
        (edge) =>
          relevantResourceIds.has(edge.to) && edge.relation !== "same-solution",
      )
      .map((edge) => ({ edge, node: nodeMap.get(edge.from) }));

    const sameSolution = visibleEdges
      .filter((edge) => edge.relation === "same-solution")
      .map((edge) => ({
        edge,
        node: relevantResourceIds.has(edge.from)
          ? nodeMap.get(edge.to)
          : nodeMap.get(edge.from),
      }));

    return {
      dependsOn,
      usedBy,
      sameSolution,
      visibleEdges,
    };
  }, [dependencies, selectedResource, selectedScopeResources]);

  const topCostResources = useMemo(() => {
    return sortResources(selectedScopeResources, "cost-desc").slice(0, 8);
  }, [selectedScopeResources]);

  const topIssueResources = useMemo(() => {
    return [...selectedScopeResources]
      .sort((a, b) => (b.findings?.length || 0) - (a.findings?.length || 0))
      .slice(0, 8);
  }, [selectedScopeResources]);

  const selectedTitle = useMemo(() => {
    if (selectedResource) return getFriendlyResourceName(selectedResource);

    const typeLabel = selectedTypeKey
      ? resourceTypeSummary.find((item) => item.key === selectedTypeKey)?.label
      : null;

    if (selectedResourceGroup && typeLabel) {
      return `${selectedResourceGroup} · ${typeLabel}`;
    }

    if (selectedSubscription && overview && typeLabel) {
      const subscriptionName =
        overview.subscriptions.find(
          (s) => s.subscriptionId === selectedSubscription,
        )?.subscriptionName || selectedSubscription;

      return `${subscriptionName} · ${typeLabel}`;
    }

    if (typeLabel) return `Azure Landscape · ${typeLabel}`;
    if (selectedResourceGroup) return selectedResourceGroup;

    if (selectedSubscription && overview) {
      return (
        overview.subscriptions.find(
          (s) => s.subscriptionId === selectedSubscription,
        )?.subscriptionName || selectedSubscription
      );
    }

    return "Azure Landscape";
  }, [
    selectedResource,
    selectedTypeKey,
    resourceTypeSummary,
    selectedResourceGroup,
    selectedSubscription,
    overview,
  ]);

  const selectedMonthlyCost = selectedScopeResources.reduce(
    (sum, resource) => sum + (resource.monthlyCost || 0),
    0,
  );

  const selectedAverageScore =
    selectedScopeResources.length === 0
      ? 0
      : Math.round(
          selectedScopeResources.reduce(
            (sum, resource) => sum + (resource.governanceScore || 0),
            0,
          ) / selectedScopeResources.length,
        );

  const maxTopCost = Math.max(
    ...topCostResources.map((r) => r.monthlyCost || 0),
    1,
  );
  const maxTopIssues = Math.max(
    ...topIssueResources.map((r) => r.findings?.length || 0),
    1,
  );

  const resetView = () => {
    setSelectedSubscription(null);
    setSelectedResourceGroup(null);
    setSelectedResource(null);
    setSelectedTypeKey(null);
    setExpandedTypeKey(null);
    setSearch("");
    setResourceDetails(null);
    setResourceDetailsError("");
  };

  const expandAll = () => {
    if (!overview) return;

    const subs: Record<string, boolean> = {};
    const groups: Record<string, boolean> = {};

    overview.subscriptions.forEach((subscription) => {
      subs[subscription.subscriptionId] = true;
    });

    overview.resourceGroups.forEach((group) => {
      groups[`${group.subscriptionId}/${group.resourceGroup}`] = true;
    });

    setOpenSubscriptions(subs);
    setOpenResourceGroups(groups);
  };

  const collapseAll = () => {
    setOpenSubscriptions({});
    setOpenResourceGroups({});
  };

  if (loading) {
    return (
      <FoundryPageShell
        frameless
        main={
          <div className="p-5">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/60">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-400">Azure operations</p>
              <div className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Loading Landscape...</div>
              <div className="mt-1 text-xs text-slate-400">
                Foundry leest subscriptions, resource groups, resources, costs
                en governance-data uit.
              </div>
            </div>
          </div>
        }
      />
    );
  }

  if (error || !overview) {
    return (
      <FoundryPageShell
        frameless
        main={
          <div className="p-5">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-200">
              <div className="font-bold">
                Landscape overview kon niet worden geladen.
              </div>
              <div className="mt-2">{error || "Geen overview data"}</div>
            </div>
          </div>
        }
      />
    );
  }

  const costWarning =
    overview.scan.costStatus === "partial" && overview.scan.costErrors?.length;

  return (
    <FoundryPageShell
      frameless
      main={
        <main className="space-y-5 overflow-y-auto p-5">
          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900/60">
            <div className="h-0.5 bg-gradient-to-r from-orange-400 via-sky-400 to-emerald-400" />
            <div className="flex flex-wrap items-end justify-between gap-4 px-5 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-400">Azure operations</p>
                <h1 className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">Landscape</h1>
                <p className="mt-1 text-sm text-slate-500">Resources, dependencies, costs and governance across your Azure subscriptions.</p>
              </div>
              <div className="flex items-center gap-2 text-[11px] font-semibold"><span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-emerald-700 dark:text-emerald-300">Live Azure data</span><span className="text-slate-500">Updated {formatDateTime(overview.scan.scannedAt)}</span></div>
            </div>
            <div className="grid grid-cols-2 divide-x divide-y border-t border-slate-200 dark:divide-white/10 dark:border-white/10 md:grid-cols-5 md:divide-y-0">
              {[
                ["Resources", overview.summaries.resourceCount, "text-orange-400"],
                ["Subscriptions", overview.summaries.subscriptionCount, "text-sky-400"],
                ["Resource groups", overview.summaries.resourceGroupCount, "text-emerald-400"],
                ["Findings", overview.summaries.findingCount, "text-rose-400"],
                ["Dependencies", dependenciesLoading ? "..." : (dependencies?.summaries?.edgeCount ?? 0), "text-violet-400"],
              ].map(([title, value, tone]) => (
                <div key={String(title)} className="px-5 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{title}</p>
                  <p className={`mt-1 text-2xl font-semibold tracking-tight ${tone}`}>{value}</p>
                </div>
              ))}
            </div>
          </section>
          {costWarning ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
              Kosten zijn gedeeltelijk geladen. Sommige subscriptions/resources
              kunnen ontbreken.
            </div>
          ) : null}

          {dependenciesError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
              Dependencies konden niet worden geladen: {dependenciesError}
            </div>
          )}

          <section className="grid grid-cols-1 gap-5 2xl:grid-cols-[430px_minmax(0,1fr)_360px]">
            <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
              <div className="border-b border-slate-100 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-950">
                      Azure Landscape
                    </h2>
                    <div className="mt-1 text-xs text-slate-400">
                      {filteredResources.length} resources zichtbaar
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={resetView}
                      className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={expandAll}
                      className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Expand
                    </button>
                    <button
                      type="button"
                      onClick={collapseAll}
                      className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Collapse
                    </button>
                  </div>
                </div>

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search resources, types, groups..."
                  className="mt-4 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#ff5a00]"
                />

                <select
                  value={resourceSort}
                  onChange={(e) =>
                    setResourceSort(e.target.value as ResourceSort)
                  }
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#ff5a00]"
                >
                  <option value="cost-desc">Kosten hoog → laag</option>
                  <option value="cost-asc">Kosten laag → hoog</option>
                  <option value="name">Naam A → Z</option>
                </select>
              </div>

              <div className="max-h-[820px] overflow-auto p-4">
                <div className="space-y-3">
                  {overview.subscriptions.map((subscription) => {
                    const groups = overview.resourceGroups.filter(
                      (g) => g.subscriptionId === subscription.subscriptionId,
                    );

                    const subscriptionOpen =
                      openSubscriptions[subscription.subscriptionId] ?? true;

                    const subscriptionSelected =
                      selectedSubscription === subscription.subscriptionId &&
                      !selectedResourceGroup &&
                      !selectedResource &&
                      !selectedTypeKey;

                    return (
                      <div
                        key={subscription.subscriptionId}
                        className="rounded-2xl border border-slate-200"
                      >
                        <button
                          onClick={() => {
                            setOpenSubscriptions((prev) => ({
                              ...prev,
                              [subscription.subscriptionId]: !subscriptionOpen,
                            }));
                            setSelectedSubscription(
                              subscription.subscriptionId,
                            );
                            setSelectedResourceGroup(null);
                            setSelectedResource(null);
                            setSelectedTypeKey(null);
                            setExpandedTypeKey(null);
                          }}
                          className={`flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left ${
                            subscriptionSelected
                              ? "bg-orange-50 ring-1 ring-orange-200"
                              : "bg-slate-50 hover:bg-slate-100"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="break-all text-sm font-bold text-slate-900">
                              {subscriptionOpen ? "▾" : "▸"} 🔷{" "}
                              {subscription.subscriptionName}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {subscription.resourceCount} resources ·{" "}
                              {subscription.governanceScore}% score
                            </div>
                          </div>

                          <div className="shrink-0 text-right">
                            <div className="text-xs font-bold text-slate-900">
                              {formatCurrency(
                                subscription.monthlyCost,
                                overview.summaries.currency,
                              )}
                            </div>
                            <div className="mt-1 text-[11px] text-red-500">
                              {subscription.findingCount} findings
                            </div>
                          </div>
                        </button>

                        {subscriptionOpen && (
                          <div className="space-y-1 p-2">
                            {groups.map((group) => {
                              const groupKey = `${group.subscriptionId}/${group.resourceGroup}`;
                              const groupOpen =
                                openResourceGroups[groupKey] ?? false;

                              const groupResources = overview.resources.filter(
                                (r) =>
                                  r.subscriptionId === group.subscriptionId &&
                                  r.resourceGroup === group.resourceGroup,
                              );

                              const visibleGroupResources = sortResources(
                                groupResources.filter((resource) => {
                                  const category = getResourceCategory(
                                    resource.type,
                                  );
                                  const friendlyName =
                                    getFriendlyResourceName(resource);

                                  if (
                                    selectedTypeKey &&
                                    category.key !== selectedTypeKey
                                  ) {
                                    return false;
                                  }

                                  if (!search.trim()) return true;

                                  const q = search.toLowerCase();

                                  return (
                                    resource.name.toLowerCase().includes(q) ||
                                    friendlyName.toLowerCase().includes(q) ||
                                    resource.type.toLowerCase().includes(q) ||
                                    category.label.toLowerCase().includes(q) ||
                                    resource.resourceGroup
                                      .toLowerCase()
                                      .includes(q) ||
                                    (resource.subscriptionName || "")
                                      .toLowerCase()
                                      .includes(q) ||
                                    resource.subscriptionId
                                      .toLowerCase()
                                      .includes(q)
                                  );
                                }),
                                resourceSort,
                              );

                              const groupSelected =
                                selectedSubscription === group.subscriptionId &&
                                selectedResourceGroup === group.resourceGroup &&
                                !selectedResource &&
                                !selectedTypeKey;

                              return (
                                <div key={groupKey} className="rounded-2xl">
                                  <button
                                    onClick={() => {
                                      setOpenResourceGroups((prev) => ({
                                        ...prev,
                                        [groupKey]: !groupOpen,
                                      }));
                                      setSelectedSubscription(
                                        group.subscriptionId,
                                      );
                                      setSelectedResourceGroup(
                                        group.resourceGroup,
                                      );
                                      setSelectedResource(null);
                                      setSelectedTypeKey(null);
                                      setExpandedTypeKey(null);
                                    }}
                                    className={`flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-2 ${
                                      groupSelected
                                        ? "bg-orange-50 ring-1 ring-orange-200"
                                        : "hover:bg-slate-50"
                                    }`}
                                  >
                                    <div className="min-w-0 text-left">
                                      <div className="break-all text-xs font-bold text-slate-800">
                                        {groupOpen ? "▾" : "▸"} 📁{" "}
                                        {group.resourceGroup}
                                      </div>
                                      <div className="mt-1 text-[11px] text-slate-400">
                                        {group.resourceCount} resources ·{" "}
                                        {group.governanceScore}% score
                                      </div>
                                    </div>

                                    <div className="shrink-0 text-right">
                                      <div className="text-[11px] font-bold text-slate-700">
                                        {formatCurrency(
                                          group.monthlyCost,
                                          overview.summaries.currency,
                                        )}
                                      </div>
                                      {group.findingCount > 0 && (
                                        <div className="mt-1 text-[10px] font-semibold text-red-500">
                                          {group.findingCount} findings
                                        </div>
                                      )}
                                    </div>
                                  </button>

                                  {groupOpen && (
                                    <div className="ml-4 space-y-1 border-l border-slate-200 pl-3">
                                      {visibleGroupResources.length === 0 ? (
                                        <div className="px-3 py-2 text-[11px] text-slate-400">
                                          Geen resources binnen filter.
                                        </div>
                                      ) : null}

                                      {visibleGroupResources.map((resource) => {
                                        const selected =
                                          selectedResource?.id ===
                                            resource.id && !!resource.id;
                                        const category = getResourceCategory(
                                          resource.type,
                                        );
                                        const friendlyName =
                                          getFriendlyResourceName(resource);

                                        return (
                                          <button
                                            key={
                                              resource.id ||
                                              `${resource.subscriptionId}-${resource.resourceGroup}-${resource.name}`
                                            }
                                            onClick={() => {
                                              setSelectedSubscription(
                                                resource.subscriptionId,
                                              );
                                              setSelectedResourceGroup(
                                                resource.resourceGroup,
                                              );
                                              setSelectedResource(resource);
                                              setSelectedTypeKey(null);
                                            }}
                                            className={`grid w-full grid-cols-[minmax(0,1fr)_92px_48px] gap-3 rounded-xl px-3 py-2 text-left ${
                                              selected
                                                ? "bg-orange-50 ring-1 ring-orange-200"
                                                : "hover:bg-slate-50"
                                            }`}
                                          >
                                            <div className="min-w-0">
                                              <div className="truncate text-[11px] font-semibold text-slate-800">
                                                {friendlyName}
                                              </div>

                                              {friendlyName !==
                                                resource.name && (
                                                <div className="mt-1 truncate text-[10px] text-slate-400">
                                                  {resource.name}
                                                </div>
                                              )}

                                              <div className="mt-1 flex items-center gap-2">
                                                <span
                                                  className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10px] font-bold ${category.tone}`}
                                                >
                                                  <span>{category.icon}</span>
                                                  <span className="truncate">
                                                    {category.label}
                                                  </span>
                                                </span>
                                              </div>
                                            </div>

                                            <div className="text-right text-[10px] font-bold text-slate-700">
                                              {formatCurrency(
                                                resource.monthlyCost || 0,
                                                resource.currency ||
                                                  overview.summaries.currency,
                                              )}
                                            </div>

                                            <div className="text-right">
                                              {(resource.findings?.length ||
                                                0) === 0 ? (
                                                <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-700">
                                                  OK
                                                </span>
                                              ) : (
                                                <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700">
                                                  {resource.findings.length}
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="space-y-5">
              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Foundry Governance
                  </div>

                  <h2 className="mt-2 break-all text-3xl font-bold tracking-tight text-slate-950">
                    {selectedTitle}
                  </h2>
                </div>

                <div className="grid grid-cols-2 gap-4 p-5 xl:grid-cols-4">
                  {[
                    ["Resources", selectedScopeResources.length],
                    ["Governance", `${selectedAverageScore}%`],
                    ["Findings", selectedScopeFindings.length],
                    [
                      "Monthly Cost",
                      formatCurrency(
                        selectedMonthlyCost,
                        overview.summaries.currency,
                      ),
                    ],
                  ].map(([title, value]) => (
                    <div
                      key={String(title)}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        {title}
                      </div>
                      <div className="mt-3 break-all text-2xl font-bold tracking-tight text-slate-950">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {selectedResource && (
                  <div className="border-t border-slate-100 p-5">
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          Original name
                        </div>
                        <div className="mt-2 break-all text-sm font-semibold text-slate-800">
                          {selectedResource.name}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          Category
                        </div>
                        <div className="mt-2">
                          {(() => {
                            const category = getResourceCategory(
                              selectedResource.type,
                            );

                            return (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${category.tone}`}
                              >
                                <span>{category.icon}</span>
                                <span>{category.label}</span>
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          Location
                        </div>
                        <div className="mt-2 break-all text-sm font-semibold text-slate-800">
                          {selectedResource.location || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                  <div>
                    <h2 className="text-sm font-bold text-slate-950">
                      Resource Types
                    </h2>
                    <div className="mt-1 text-xs text-slate-400">
                      Klik op een type om te zien welke resources erin zitten.
                    </div>
                  </div>

                  {selectedTypeKey && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTypeKey(null);
                        setExpandedTypeKey(null);
                      }}
                      className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Clear type
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 p-5 xl:grid-cols-2">
                  {resourceTypeSummary.length === 0 ? (
                    <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                      Geen resource types gevonden.
                    </div>
                  ) : null}

                  {resourceTypeSummary.map((item) => {
                    const selected = selectedTypeKey === item.key;
                    const expanded = expandedTypeKey === item.key;

                    const typeResources = sortResources(
                      resourcesInSelectedScopeBeforeType.filter(
                        (resource) =>
                          getResourceCategory(resource.type).key === item.key,
                      ),
                      resourceSort,
                    );

                    return (
                      <div
                        key={item.key}
                        className={`rounded-2xl border bg-white transition ${
                          selected
                            ? "border-orange-200 ring-1 ring-orange-200"
                            : "border-slate-200"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedTypeKey(expanded ? null : item.key);
                            setSelectedTypeKey(item.key);
                            setSelectedResource(null);
                          }}
                          className={`w-full rounded-2xl p-4 text-left transition ${
                            selected ? "bg-orange-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${item.tone}`}
                              >
                                <span>{expanded ? "▾" : "▸"}</span>
                                <span>{item.icon}</span>
                                <span>{item.label}</span>
                              </span>
                              <div className="mt-2 text-[11px] text-slate-400">
                                {item.description}
                              </div>
                            </div>

                            <div className="shrink-0 text-right">
                              <div className="text-2xl font-bold text-slate-950">
                                {item.count}
                              </div>
                              <div className="text-[10px] font-semibold uppercase text-slate-400">
                                resources
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
                            <div className="rounded-xl bg-slate-50 px-3 py-2 font-semibold text-slate-600">
                              {formatCurrency(
                                item.monthlyCost,
                                overview.summaries.currency,
                              )}
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2 text-right font-semibold text-red-500">
                              {item.findings} findings
                            </div>
                          </div>
                        </button>

                        {expanded && (
                          <div className="max-h-72 space-y-1 overflow-auto border-t border-slate-100 p-3">
                            {typeResources.length === 0 ? (
                              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
                                Geen resources gevonden voor dit type.
                              </div>
                            ) : null}

                            {typeResources.map((resource) => {
                              const active =
                                selectedResource?.id === resource.id &&
                                !!resource.id;
                              const friendlyName =
                                getFriendlyResourceName(resource);

                              return (
                                <button
                                  key={
                                    resource.id ||
                                    `${resource.subscriptionId}-${resource.resourceGroup}-${resource.name}`
                                  }
                                  type="button"
                                  onClick={() => {
                                    setSelectedSubscription(
                                      resource.subscriptionId,
                                    );
                                    setSelectedResourceGroup(
                                      resource.resourceGroup,
                                    );
                                    setSelectedResource(resource);
                                    setSelectedTypeKey(item.key);
                                  }}
                                  className={`w-full rounded-xl px-3 py-2 text-left ${
                                    active
                                      ? "bg-orange-50 ring-1 ring-orange-200"
                                      : "hover:bg-slate-50"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="truncate text-[11px] font-bold text-slate-800">
                                        {friendlyName}
                                      </div>

                                      {friendlyName !== resource.name && (
                                        <div className="mt-1 truncate text-[10px] text-slate-400">
                                          {resource.name}
                                        </div>
                                      )}

                                      <div className="mt-1 truncate text-[10px] text-slate-400">
                                        {resource.resourceGroup}
                                      </div>
                                    </div>

                                    <div className="shrink-0 text-right text-[10px] font-semibold text-slate-500">
                                      {formatCurrency(
                                        resource.monthlyCost || 0,
                                        resource.currency ||
                                          overview.summaries.currency,
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <ResourceDetailsPanel
                details={resourceDetails}
                loading={resourceDetailsLoading}
                error={resourceDetailsError}
              />

              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-950">
                    Dependencies
                  </h2>
                </div>

                <div className="space-y-4 p-5">
                  {dependenciesLoading && (
                    <div className="text-sm text-slate-500">
                      Dependencies laden...
                    </div>
                  )}

                  {!dependenciesLoading && !dependencies && (
                    <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                      Geen dependency-data geladen.
                    </div>
                  )}

                  {dependencies && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-[10px] font-bold uppercase text-slate-400">
                            Nodes
                          </div>
                          <div className="mt-1 text-lg font-bold">
                            {dependencies.summaries.nodeCount}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-[10px] font-bold uppercase text-slate-400">
                            Edges
                          </div>
                          <div className="mt-1 text-lg font-bold">
                            {dependencyContext.visibleEdges.length}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-[10px] font-bold uppercase text-slate-400">
                            High
                          </div>
                          <div className="mt-1 text-lg font-bold">
                            {
                              dependencyContext.visibleEdges.filter(
                                (edge) => edge.confidence === "high",
                              ).length
                            }
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                          Depends on
                        </div>

                        {dependencyContext.dependsOn.length === 0 ? (
                          <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-500">
                            Geen directe dependencies gevonden.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {dependencyContext.dependsOn
                              .slice(0, 8)
                              .map(({ edge, node }) => {
                                const category = getResourceCategory(
                                  node?.type,
                                );
                                const friendlyName = node
                                  ? getFriendlyResourceName(node)
                                  : edge.to;

                                return (
                                  <div
                                    key={`${edge.from}-${edge.to}-${edge.relation}`}
                                    className="rounded-2xl border border-slate-200 p-3"
                                  >
                                    <div className="break-all text-sm font-bold text-slate-900">
                                      {friendlyName}
                                    </div>

                                    {node && friendlyName !== node.name && (
                                      <div className="mt-1 break-all text-[10px] text-slate-400">
                                        {node.name}
                                      </div>
                                    )}

                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${category.tone}`}
                                      >
                                        {category.icon}{" "}
                                        {node?.type
                                          ? category.label
                                          : "Unknown"}
                                      </span>
                                      <span>{edge.relation}</span>
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${confidenceClass(
                                          edge.confidence,
                                        )}`}
                                      >
                                        {edge.confidence}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                          Used by
                        </div>

                        {dependencyContext.usedBy.length === 0 ? (
                          <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-500">
                            Geen inkomende dependencies gevonden.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {dependencyContext.usedBy
                              .slice(0, 8)
                              .map(({ edge, node }) => {
                                const category = getResourceCategory(
                                  node?.type,
                                );
                                const friendlyName = node
                                  ? getFriendlyResourceName(node)
                                  : edge.from;

                                return (
                                  <div
                                    key={`${edge.from}-${edge.to}-${edge.relation}`}
                                    className="rounded-2xl border border-slate-200 p-3"
                                  >
                                    <div className="break-all text-sm font-bold text-slate-900">
                                      {friendlyName}
                                    </div>

                                    {node && friendlyName !== node.name && (
                                      <div className="mt-1 break-all text-[10px] text-slate-400">
                                        {node.name}
                                      </div>
                                    )}

                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${category.tone}`}
                                      >
                                        {category.icon}{" "}
                                        {node?.type
                                          ? category.label
                                          : "Unknown"}
                                      </span>
                                      <span>{edge.relation}</span>
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${confidenceClass(
                                          edge.confidence,
                                        )}`}
                                      >
                                        {edge.confidence}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>

                      {dependencyContext.sameSolution.length > 0 && (
                        <div>
                          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                            Same solution signals
                          </div>

                          <div className="space-y-2">
                            {dependencyContext.sameSolution
                              .slice(0, 6)
                              .map(({ edge, node }) => {
                                const category = getResourceCategory(
                                  node?.type,
                                );
                                const friendlyName = node
                                  ? getFriendlyResourceName(node)
                                  : edge.to;

                                return (
                                  <div
                                    key={`${edge.from}-${edge.to}-${edge.source}`}
                                    className="rounded-2xl border border-slate-200 p-3"
                                  >
                                    <div className="break-all text-sm font-bold text-slate-900">
                                      {friendlyName}
                                    </div>

                                    {node && friendlyName !== node.name && (
                                      <div className="mt-1 break-all text-[10px] text-slate-400">
                                        {node.name}
                                      </div>
                                    )}

                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${category.tone}`}
                                      >
                                        {category.icon}{" "}
                                        {node?.type
                                          ? category.label
                                          : "Unknown"}
                                      </span>
                                      <span>{edge.relation}</span>
                                      <span
                                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${confidenceClass(
                                          edge.confidence,
                                        )}`}
                                      >
                                        {edge.confidence}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-950">
                    Top Resources by Cost
                  </h2>
                </div>

                <div className="space-y-4 p-5">
                  {topCostResources.length === 0 && (
                    <div className="text-sm text-slate-500">
                      Geen resources gevonden.
                    </div>
                  )}

                  {topCostResources.map((resource) => {
                    const cost = resource.monthlyCost || 0;
                    const category = getResourceCategory(resource.type);
                    const friendlyName = getFriendlyResourceName(resource);

                    return (
                      <div key={resource.id || resource.name}>
                        <div className="mb-2 flex justify-between gap-3 text-xs">
                          <span className="truncate font-semibold text-slate-700">
                            {category.icon} {friendlyName}
                          </span>
                          <span className="text-slate-500">
                            {formatCurrency(
                              cost,
                              resource.currency || overview.summaries.currency,
                            )}
                          </span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-[#ff5a00]"
                            style={{
                              width: `${Math.round(
                                (cost / Math.max(maxTopCost, 1)) * 100,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </section>

            <section className="space-y-5">
              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-950">Findings</h2>
                </div>

                <div className="space-y-3 p-5">
                  {selectedScopeFindings.length === 0 && (
                    <div className="rounded-2xl bg-emerald-50 p-4 text-sm font-semibold text-emerald-700">
                      Geen findings in deze selectie.
                    </div>
                  )}

                  {selectedScopeFindings.slice(0, 10).map((finding, index) => (
                    <div
                      key={`${finding.resourceId}-${finding.ruleId}-${index}`}
                      className="rounded-2xl border border-slate-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-bold text-slate-900">
                            {finding.title}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {finding.resourceFriendlyName ||
                              finding.resourceName}
                          </div>
                          <div className="mt-3 text-[11px] text-slate-400">
                            {finding.recommendation}
                          </div>
                        </div>

                        <div
                          className={`rounded-full px-2 py-1 text-[10px] font-bold ${severityClass(
                            finding.severity,
                          )}`}
                        >
                          {finding.severity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-950">
                    Top Issue Resources
                  </h2>
                </div>

                <div className="space-y-4 p-5">
                  {topIssueResources.length === 0 ? (
                    <div className="text-sm text-slate-500">
                      Geen resources gevonden.
                    </div>
                  ) : null}

                  {topIssueResources.map((resource) => {
                    const issues = resource.findings?.length || 0;
                    const category = getResourceCategory(resource.type);
                    const friendlyName = getFriendlyResourceName(resource);

                    return (
                      <div key={resource.id || resource.name}>
                        <div className="mb-2 flex justify-between gap-3 text-xs">
                          <span className="truncate font-semibold text-slate-700">
                            {category.icon} {friendlyName}
                          </span>
                          <span className="text-slate-500">{issues}</span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-red-500"
                            style={{
                              width: `${Math.round(
                                (issues / Math.max(maxTopIssues, 1)) * 100,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/60">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h2 className="text-sm font-bold text-slate-950">
                    Scan Information
                  </h2>
                </div>

                <div className="space-y-4 p-5 text-sm">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                      Source
                    </div>
                    <div className="mt-1 font-semibold text-slate-800">
                      {overview.scan.source}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                      Scanned At
                    </div>
                    <div className="mt-1 font-semibold text-slate-800">
                      {formatDateTime(overview.scan.scannedAt)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                      Cost Status
                    </div>
                    <div className="mt-1 font-semibold text-slate-800">
                      {overview.scan.costStatus}
                    </div>
                  </div>

                  {dependencies && (
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Dependency Scan
                      </div>
                      <div className="mt-1 font-semibold text-slate-800">
                        {dependencies.scan.source} ·{" "}
                        {formatDateTime(dependencies.scan.scannedAt)}
                      </div>
                    </div>
                  )}

                  {resourceDetails && (
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Resource Detail Scan
                      </div>
                      <div className="mt-1 font-semibold text-slate-800">
                        {resourceDetails.source} ·{" "}
                        {formatDateTime(resourceDetails.scannedAt)}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </section>
          </section>
        </main>
      }
    />
  );
};

export default LandscapePage;
